// The error boundary between `api/` and the store (contract §6). Everything that can reject lives
// behind this file; nothing above it sees an `ApiError`.
//
// WHERE THE BOOT AND SWITCH REFRESH ACTUALLY COMES FROM — not from here. `'integrations'` is in 07's
// `WORKSPACE_LEVEL_SECTIONS` (`flowToolkit.ts:49-55`), so `SelectWorkspaceFlow` already resets the
// section and re-dispatches it on every workspace switch, and the boot flow hydrates it once. The
// v3 arrangement — `workspaceStore.ts:420-421` calling `getIntegrations(reload)` +
// `getConnectableIntegrations(reload)` on workspace composition — is REPLACED by that dispatch, and
// calling `refreshAll` from 07 as well would double-fetch every switch.
//
// So `refreshAll` exists for the paths the bootstrap cannot cover: a refresh after the user connects
// or removes an integration. v3's other two callers do not survive — the Connections page's
// `force:true` fetches on mount are defect D9 (the bootstrap already hydrated all four slices) and
// the onboarding modal's are the same call.
import { IntegrationApi } from '../api/IntegrationApi'
import { useIntegrationStore } from '../stores/integrationStore'

import { useAccount, useWorkspace } from '@/features/account'

/** Which account id the integration endpoints are addressed with — v3 `integrationStore.ts:252-254`. */
function resolveAccountId(): number | string | undefined {
  const workspace = useWorkspace()
  const account = useAccount()
  return workspace.workspace.value?.is_workspace_admin
    ? workspace.workspace.value.account_id
    : account.accountId.value
}

export interface RefreshOutcome {
  /** False when any attempted read failed. Warm state is retained either way. */
  ok: boolean
  /** Reads that actually went to the network (a short-circuited read is not "attempted"). */
  attempted: number
}

/**
 * Re-read the three page-level slices.
 *
 * Each read keeps its own v3 short-circuit: `allowed`/`all` skip when `integrationGroups` is
 * already populated, `connectable` skips when it is — unless `force`.
 *
 * ON FAILURE THE WARM SLICE IS RETAINED, deliberately diverging from v3, which assigned `null` to
 * `allIntegrations`/`connectableIntegrations` inside its `catch` and so blanked a working
 * Connections page on any transient 500. Retaining matches this module's own hydrator convention
 * (`bootstrapHydrators.ts`: "`null` means failed/cold: retain warm state"), and the caller learns
 * about the failure from the return value instead of from an empty screen.
 */
export async function refreshAll(force = false): Promise<RefreshOutcome> {
  const store = useIntegrationStore()
  const workspace = useWorkspace()
  const account = useAccount()

  const workspaceId = workspace.workspaceId.value
  const accountId = resolveAccountId()
  const userId = account.userId.value
  const isWorkspaceAdmin = Boolean(workspace.workspace.value?.is_workspace_admin)

  const wantsList = force || !hasEntries(store.integrationGroups)
  const wantsConnectable = force || !hasEntries(store.connectableIntegrations)

  const tasks: Array<Promise<boolean>> = []

  if (wantsList && workspaceId !== undefined) {
    tasks.push(
      guard(async () => {
        const allowed = await IntegrationApi.getAllowedIntegrations(workspaceId)
        store.setAllowedIntegrations(allowed.allowed_integrations, allowed.integration_groups)
      }),
    )
  }
  if (wantsList && accountId !== undefined) {
    tasks.push(
      guard(async () => {
        store.setAllIntegrations(await IntegrationApi.getAllIntegrations(accountId))
      }),
    )
  }
  // v3 required BOTH ids before it would issue this one (`integrationStore.ts:312`).
  if (wantsConnectable && accountId !== undefined && userId !== undefined) {
    tasks.push(
      guard(async () => {
        store.setConnectableIntegrations(
          await IntegrationApi.getConnectableIntegrations(accountId, userId, isWorkspaceAdmin),
        )
      }),
    )
  }

  const results = await Promise.all(tasks)
  return { ok: results.every(Boolean), attempted: results.length }
}

/**
 * Re-read the account-wide connection counter (ORP-21468).
 *
 * Separate from `refreshAll` on purpose: this is a FOURTH endpoint, and the three in `refreshAll`
 * already run on every boot and every workspace switch through 07's bootstrap section. Only the
 * screens that print the counter pay for it, and only when they mount.
 *
 * Same failure contract as its neighbours — the rejection stops here and the warm value is
 * retained, so a transient 500 leaves the meter showing the last number it knew rather than
 * blanking it.
 */
export async function refreshConnectionUsage(): Promise<boolean> {
  const store = useIntegrationStore()
  const workspaceId = useWorkspace().workspaceId.value
  if (workspaceId === undefined) return false
  return guard(async () => {
    const detail = await IntegrationApi.getWorkspaceDetail(workspaceId)
    if (detail !== null) store.setConnectionUsage(detail)
  })
}

/**
 * Disconnect ad accounts, then re-read what the screen renders.
 *
 * The two follow-up reads are forced: `refreshAll(true)` because the removed rows are still in the
 * warm slice, and the counter because a freed slot changes what the pickers may offer next.
 * Rejections propagate — the caller is a user-initiated write with a confirmation behind it, and it
 * must be able to say the removal failed.
 */
export async function removeAdAccounts(
  accountIds: readonly (number | string)[],
): Promise<RefreshOutcome> {
  const workspaceId = useWorkspace().workspaceId.value
  if (workspaceId === undefined) throw new Error('Removing an ad account requires a workspace')
  await IntegrationApi.deleteAdAccounts(workspaceId, accountIds)
  const outcome = await refreshAll(true)
  await refreshConnectionUsage()
  return outcome
}

function hasEntries(value: Record<string, unknown> | null): boolean {
  return value !== null && Object.keys(value).length > 0
}

/** Runs a write and reports success. The rejection stops here — that is this file's whole job. */
async function guard(run: () => Promise<void>): Promise<boolean> {
  try {
    await run()
    return true
  } catch {
    return false
  }
}
